import React, { useEffect } from "react";
import { Outlet, NavLink, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ShieldCheck, Calculator, FolderKanban, GitCompareArrows, Settings2, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { Logo, LogoMark } from "@/components/Logo";

const NAV = [
  { to: "/calculateur", label: "Calculateur", icon: Calculator, testid: "nav-calculateur-link" },
  { to: "/dossiers", label: "Dossiers", icon: FolderKanban, testid: "nav-dossiers-link" },
  { to: "/comparateur", label: "Comparateur", icon: GitCompareArrows, testid: "nav-comparateur-link" },
];

function Lenis() {
  useEffect(() => {
    let raf;
    let lenis;
    (async () => {
      const { default: Lenis } = await import("lenis");
      lenis = new Lenis({ duration: 1.15, smoothWheel: true });
      const loop = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(loop);
      };
      raf = requestAnimationFrame(loop);
    })();
    return () => {
      cancelAnimationFrame(raf);
      lenis?.destroy();
    };
  }, []);
  return null;
}

export const RoleChip = () => {
  const { isAdmin } = useAuth();
  return (
    <span
      data-testid={isAdmin ? "role-chip-admin" : "role-chip-user"}
      className={`hidden sm:inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-widest ${
        isAdmin ? "border-amber-500/40 bg-amber-500/10 text-[var(--amber-text)]" : "border-sky-500/40 bg-sky-500/10 text-[var(--accent-text)]"
      }`}
    >
      <ShieldCheck size={12} />
      {isAdmin ? "Administrateur" : "Partenaire"}
    </span>
  );
};

const Shell = () => {
  const { user, isAdmin, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const navItems = isAdmin ? [...NAV, { to: "/admin", label: "Administration", icon: Settings2, testid: "nav-admin-link" }] : NAV;

  return (
    <div className="grain min-h-screen">
      <Lenis />
      <header className="glass no-print fixed inset-x-0 top-0 z-40 border-b border-[var(--line)]">
        <div className="mx-auto flex h-16 max-w-[1440px] items-center gap-3 px-4 sm:gap-4 sm:px-6">
          <Link to="/calculateur" data-testid="header-logo-link" className="shrink-0">
            <Logo />
          </Link>
          <nav className="ml-auto hidden items-center gap-1 md:flex">
            {navItems.map(({ to, label, icon: Icon, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-medium transition-all duration-200 ${
                    isActive
                      ? "bg-sky-500/12 text-[var(--accent-text)] shadow-[inset_0_0_0_1px_rgba(36,160,247,0.35)]"
                      : "text-[var(--dim)] hover:bg-white/5 hover:text-[var(--ink)]"
                  }`
                }
              >
                <Icon size={15} />
                {label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-3 md:ml-2">
            <button
              data-testid="theme-toggle-btn"
              onClick={toggleTheme}
              title={theme === "dark" ? "Passer en interface claire" : "Passer en interface sombre"}
              aria-label={theme === "dark" ? "Passer en interface claire" : "Passer en interface sombre"}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--line)] text-[var(--dim)] transition-all hover:border-[var(--line-hover)] hover:text-[var(--ink)]"
            >
              {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
            </button>
            <RoleChip />
            <button
              data-testid="logout-btn"
              onClick={async () => {
                await logout();
                navigate("/login");
              }}
              title="Se déconnecter"
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-[var(--line)] text-[var(--dim)] transition-all hover:border-rose-500/40 hover:text-[var(--rose-text)]"
            >
              <LogOut size={15} />
            </button>
          </div>
        </div>
        <nav className="no-scrollbar flex items-center gap-1 overflow-x-auto border-t border-[var(--line-soft)] px-4 py-2 md:hidden">
          {navItems.map(({ to, label, testid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={testid}
              className={({ isActive }) =>
                `whitespace-nowrap rounded-lg px-3.5 py-1.5 text-xs font-medium ${
                  isActive ? "bg-sky-500/15 text-[var(--accent-text)]" : "text-[var(--dim)]"
                }`
              }
            >
              {label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="pt-[92px] md:pt-16">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
          <Outlet />
        </motion.div>
      </main>
      <footer className="no-print border-t border-[var(--line-soft)] py-8">
        <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-3 px-6 text-[11px] font-mono uppercase tracking-widest text-[var(--mute)] sm:flex-row">
          <span className="flex items-center gap-2">
            <LogoMark size={16} /> THIONNET SERVICE INDUSTRIE — Portail CEE
          </span>
          <span>Fiches IND-UT-102 · IND-UT-114 · IND-UT-132 — version en vigueur</span>
          <span data-testid="footer-genis">App Genis — propriété de Genis</span>
        </div>
      </footer>
    </div>
  );
};

export default Shell;
